import React from 'react';
import {
  View,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { StackingRow } from '@/components/shared/redesign/StackingRow';

interface PaymentDetailsSummaryProps {
  paymentAmount: number;
  fee: number;
  totalAmount: number;
  paymentMethodLabel: string;
  residentName?: string;
  containerStyle?: object;
}

/**
 * Reusable payment details summary component
 * Used in ConfirmPaymentContent and TransactionReceivedContent
 */
export const PaymentDetailsSummary: React.FC<PaymentDetailsSummaryProps> = ({
  paymentAmount,
  fee,
  totalAmount,
  paymentMethodLabel,
  residentName,
  containerStyle,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();

  return (
    <View style={[styles.detailsContainer, containerStyle]}>
      {/* Payment on behalf of */}
      <StackingRow style={styles.detailRow} stackedStyle={styles.rowStacked}>
        <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Payment on behalf of</Text>
        <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textPrimary }]}>{residentName || 'Yourself'}</Text>
      </StackingRow>

      {/* Payment amount */}
      <StackingRow style={styles.detailRow} stackedStyle={styles.rowStacked}>
        <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Payment amount</Text>
        <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textPrimary }]}>{paymentAmount.toFixed(2)}</Text>
      </StackingRow>

      {/* Fee */}
      <StackingRow style={styles.detailRow} stackedStyle={styles.rowStacked}>
        <Text style={[styles.detailLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>Fee</Text>
        <Text style={[styles.detailValue, { color: MARKETPLACE_COLORS.textPrimary }]}>{fee.toFixed(2)}</Text>
      </StackingRow>

      {/* Total */}
      <StackingRow style={styles.totalRow} stackedStyle={styles.rowStacked}>
        <Text style={[styles.totalLabel, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Total charges to {paymentMethodLabel}
        </Text>
        <Text style={[styles.totalValue, { color: MARKETPLACE_COLORS.textPrimary }]}>
          $ {totalAmount.toFixed(2)}
        </Text>
      </StackingRow>
    </View>
  );
};

const styles = StyleSheet.create({
  detailsContainer: {
    gap: 4,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 2,
  },
  rowStacked: {
    gap: 2,
  },
  detailLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  detailValue: {
    flexShrink: 0,
    textAlign: 'right',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 2,
    marginTop: 16,
  },
  totalLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  totalValue: {
    flexShrink: 0,
    textAlign: 'right',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
});

export default PaymentDetailsSummary;
